import axios from 'axios';

// 从localStorage获取API key
const getApiKey = () => {
  return localStorage.getItem('openai_api_key') || '';
};

// 设置API key
export const setApiKey = (key) => {
  localStorage.setItem('openai_api_key', key);
};

// 检查是否配置了API key
export const isApiKeyConfigured = () => {
  return !!getApiKey();
};

// 向OpenAI Router发送请求
export const callOpenAI = async (prompt, temperature = 0.7, model = 'gpt-3.5-turbo') => {
  const apiKey = getApiKey();
  
  if (!apiKey) {
    throw new Error('未配置API密钥');
  }
  
  try {
    const response = await axios.post('https://openrouter.ai/api/v1/chat/completions', {
      model: model,
      messages: [
        { role: "system", content: "你是一个编程教学助手，专长于发现并修复代码错误。" },
        { role: "user", content: prompt }
      ],
      temperature: temperature,
    }, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`,
        'HTTP-Referer': window.location.origin,
        'X-Title': 'CodeFix Battle'
      }
    });
    
    return response.data.choices[0].message.content;
  } catch (error) {
    console.error('调用AI服务出错:', error);
    throw error;
  }
};

// 生成包含Bug的代码题目
export const generateCodingChallenge = async (difficulty = 'medium', language = 'c') => {
  // 根据语言调整提示词
  let languageDisplay = language;
  switch(language) {
    case 'cpp':
      languageDisplay = 'C++';
      break;
    case 'javascript':
      languageDisplay = 'JavaScript';
      break;
    default:
      languageDisplay = language.charAt(0).toUpperCase() + language.slice(1);
  }
  
  // 构建针对特定语言的提示
  const prompt = `请生成一个包含常见编程错误的${languageDisplay}语言代码片段，难度级别：${difficulty}。
  
  代码应该符合${languageDisplay}语言的特性和常见问题，如:
  ${language === 'python' ? '- 缩进错误\n  - 缺少冒号\n  - 变量未定义\n  - 列表索引越界' : ''}
  ${language === 'javascript' ? '- 缺少分号\n  - 变量作用域问题\n  - undefined错误\n  - 对象属性访问错误' : ''}
  ${language === 'java' ? '- 缺少分号\n  - 类型转换错误\n  - 未捕获异常\n  - 方法签名错误' : ''}
  ${language === 'cpp' || language === 'c' ? '- 缺少分号\n  - 内存管理问题\n  - 指针错误\n  - 变量未初始化' : ''}
  
  返回格式应为JSON，包含以下字段：
  1. buggyCode - 包含bug的代码
  2. bugDescription - bug的详细描述
  3. fixedCode - 修复后的代码
  4. hint - 给用户的提示（可选）
  
  请确保代码可编译但会有运行问题，适合${difficulty}难度级别。`;
  
  try {
    const result = await callOpenAI(prompt, 0.8);
    return JSON.parse(result);
  } catch (error) {
    console.error('生成代码挑战失败:', error);
    // 如果失败，返回特定语言的默认挑战
    return getDefaultChallengeForLanguage(language);
  }
};

// 为不同语言提供默认的挑战
function getDefaultChallengeForLanguage(language) {
  switch(language) {
    case 'python':
      return {
        buggyCode: `# 这里有一个bug\ndef calculate_sum():\n    total = 0\n    for i in range(1, 6)\n        total += i\n    return total\n\nprint(calculate_sum())`,
        bugDescription: "缺少冒号",
        fixedCode: `# 修复后的代码\ndef calculate_sum():\n    total = 0\n    for i in range(1, 6):\n        total += i\n    return total\n\nprint(calculate_sum())`,
        hint: "检查for循环语法"
      };
    case 'javascript':
      return {
        buggyCode: `// 这里有一个bug\nfunction calculateSum() {\n    let total = 0;\n    for (let i = 1; i <= 5 i++) {\n        total += i;\n    }\n    return total;\n}\n\nconsole.log(calculateSum());`,
        bugDescription: "缺少分号",
        fixedCode: `// 修复后的代码\nfunction calculateSum() {\n    let total = 0;\n    for (let i = 1; i <= 5; i++) {\n        total += i;\n    }\n    return total;\n}\n\nconsole.log(calculateSum());`,
        hint: "检查for循环语法"
      };
    case 'java':
      return {
        buggyCode: `public class Main {\n    public static void main(String[] args) {\n        // 这里有一个bug\n        System.out.println("Sum: " + calculateSum())\n    }\n    \n    public static int calculateSum() {\n        int total = 0;\n        for (int i = 1; i <= 5; i++) {\n            total += i;\n        }\n        return total;\n    }\n}`,
        bugDescription: "缺少分号",
        fixedCode: `public class Main {\n    public static void main(String[] args) {\n        // 修复后的代码\n        System.out.println("Sum: " + calculateSum());\n    }\n    \n    public static int calculateSum() {\n        int total = 0;\n        for (int i = 1; i <= 5; i++) {\n            total += i;\n        }\n        return total;\n    }\n}`,
        hint: "检查println语句"
      };
    case 'cpp':
      return {
        buggyCode: `#include <iostream>\n\nint main() {\n    // 这里有一个bug\n    int sum = 0\n    for (int i = 1; i <= 5; i++) {\n        sum += i;\n    }\n    std::cout << "Sum: " << sum << std::endl;\n    return 0;\n}`,
        bugDescription: "缺少分号",
        fixedCode: `#include <iostream>\n\nint main() {\n    // 修复后的代码\n    int sum = 0;\n    for (int i = 1; i <= 5; i++) {\n        sum += i;\n    }\n    std::cout << "Sum: " << sum << std::endl;\n    return 0;\n}`,
        hint: "检查变量声明"
      };
    case 'c':
    default:
      return {
        buggyCode: `#include <stdio.h>\n\nint main() {\n    // 这里有一个bug\n    int a = 10\n    printf("a = %d\\n", a);\n    return 0;\n}`,
        bugDescription: "缺少分号",
        fixedCode: `#include <stdio.h>\n\nint main() {\n    // 修复后的代码\n    int a = 10;\n    printf("a = %d\\n", a);\n    return 0;\n}`,
        hint: "检查变量声明"
      };
  }
}

// 使用AI评估用户代码
export const evaluateCode = async (originalCode, userCode, fixedCode) => {
  const prompt = `我需要你评估用户对代码的修复。
  
  原始有bug的代码:
  \`\`\`
  ${originalCode}
  \`\`\`
  
  用户修复后的代码:
  \`\`\`
  ${userCode}
  \`\`\`
  
  正确修复后的代码:
  \`\`\`
  ${fixedCode}
  \`\`\`
  
  请分析用户是否正确修复了所有bug，返回JSON格式结果，包含:
  1. fixed (boolean): 用户是否修复了所有问题
  2. score (number): 分数(0-100)
  3. feedback (string): 对用户代码的反馈
  4. detailedAnalysis (string): 详细分析`;
  
  try {
    const result = await callOpenAI(prompt, 0.3);
    return JSON.parse(result);
  } catch (error) {
    console.error('评估代码失败:', error);
    // 如果AI评估失败，回退到简单评估
    const isFixed = userCode.includes(';') && !originalCode.includes(';');
    return {
      fixed: isFixed,
      score: isFixed ? 100 : 0,
      feedback: isFixed ? "成功修复了代码!" : "代码仍有错误",
      detailedAnalysis: isFixed ? "正确添加了缺失的分号" : "没有添加缺失的分号"
    };
  }
}; 